<script>
import useVuelidate from '@vuelidate/core';
import {required, helpers, maxLength} from '@vuelidate/validators';

/* COMPOSANTS */
import AkFormSubmitted from '@components/general/AkFormSubmitted';
import AkDropdown from '@components/v2/input/AkDropdown';
import AkInputMoney from '@components/v2/input/AkInputMoney';
import AkInputText from '@components/v2/input/AkInputText';
import AkAddressFull from '@components/v2/input/AkAddressFull';
import AkButtonValidation from '@components/v2/general/AkButtonValidation';
import GaView from '@components/v2/layout/GaView';
import AkInputPhone from '@components/v2/input/AkInputPhone.vue';

/* MIXINS */
import permissionsMixin from '@mixins/permissionsMixin';
import messageMixin from '@mixins/messageMixin';
import backMixin from '@mixins/backMixin';
import formatMixin from '@mixins/formatMixin';
import utilsMixin from '@mixins/utilsMixin';
import validatorMixins from '@mixins/validatorMixins';
import civilitiesConst from '@mixins/const/civilitiesConst';

/* SERVICES */
import commercialService from '@services/commercialService';
import itemLabelService from '@services/itemLabelService';

export default {
  components: {
    GaView,
    AkButtonValidation,
    AkAddressFull,
    AkInputText,
    AkInputMoney,
    AkDropdown,
    AkFormSubmitted,
    AkInputPhone,
  },
  mixins: [permissionsMixin, messageMixin, utilsMixin, formatMixin, backMixin, validatorMixins, civilitiesConst],
  setup() {
    return {v$: useVuelidate()};
  },
  head() {
    return {
      title: 'commercial.create',
    };
  },
  validations() {
    return {
      commercial: {
        contact: {
          companyName: {required},
          formeJuridiqueId: {required},
          raisonSociale: {required},
          email: {
            required,
            maxLengthValue: maxLength(100),
            email: helpers.withMessage(
              "Le format de l'adresse e-mail est invalide. Veuillez vérifier et réessayer (ex. : nom@domaine.com)",
              this.emailValidator,
            ),
          },
          phone: {required, phoneValidator: this.phoneValidator},
          civility: {required},
          firstName: {
            required,
            maxLengthValue: maxLength(100),
            customPattern: helpers.withMessage(this.$t('bad_text_format'), this.yousignValidator),
          },
          lastName: {
            required,
            maxLengthValue: maxLength(100),
            customPattern: helpers.withMessage(this.$t('bad_text_format'), this.yousignValidator),
          },
        },
      },
    };
  },
  data() {
    return {
      submitted: false,
      listFormeJuridique: [],
      commercial: {
        contact: {
          companyName: null,
          formeJuridiqueId: null,
          raisonSociale: null,
          capital: null,
          address: null,
          complement: null,
          postalCode: null,
          city: null,
          state: null,
          country: null,
          email: null,
          phone: null,
          villeRcs: null,
          siret: null,
          civility: null,
          lastName: null,
          firstName: null,
          function: null,
        },
      },
    };
  },
  mounted() {
    this.initGoBack();

    itemLabelService.itemLabels({type: 'FORME_JURIDIQUE'}).then((data) => {
      this.listFormeJuridique = data;
    });
  },
  methods: {
    createCommercial() {
      this.submitted = true;
      this.v$.$touch();
      if (this.v$.$error) {
        this.$refs.btnCreate.init();
        this.addError(this.$t('error.check_fields'));
        return;
      }

      commercialService
        .create(this.commercial)
        .then(() => {
          this.success(this.$t('commercial.created'));
          this.initCommercial();
        })
        .catch((e) => {
          let message = this.msgForErr(this.$t, e);
          this.addError(message);
        })
        .finally(() => {
          this.submitted = false;
          this.$refs.btnCreate.init();
        });
    },
    initCommercial() {
      this.commercial = {
        contact: {
          companyName: null,
          formeJuridiqueId: null,
          raisonSociale: null,
          capital: null,
          address: null,
          complement: null,
          postalCode: null,
          city: null,
          state: null,
          country: null,
          email: null,
          phone: null,
          villeRcs: null,
          siret: null,
          civility: null,
          lastName: null,
          firstName: null,
          function: null,
        },
      };
    },
  },
  computed: {
    isLoadingFormeJuridique() {
      return this.listFormeJuridique === undefined;
    },
  },
};
</script>

<template>
  <GaView details>
    <template #content>
      <AkFormSubmitted :disabled="false" :submitted="submitted" class="flex flex-col md:gap-4 gap-2" reference="form">
        <!-- Formulaire contact -->

        <span class="title-border secondary text-grey-900 text-xl font-bold mb-3">Contact</span>
        <div class="grid lg:grid-cols-4 md:grid-cols-2 grid-cols-1 md:gap-4 gap-2">
          <AkDropdown
            v-model="commercial.contact.formeJuridiqueId"
            :label="$t('contact.formeJuridique')"
            :loading="isLoadingFormeJuridique"
            :options="this.listFormeJuridique"
            :validator="v$.commercial.contact.formeJuridiqueId"
            optionLabel="label"
            optionValue="id" />
          <AkInputText
            v-model="commercial.contact.raisonSociale"
            :label="$t('contact.raisonSociale')"
            :formatter="toUppercase"
            :validator="v$.commercial.contact.raisonSociale" />
          <AkInputText
            v-model="commercial.contact.companyName"
            :label="$t('contact.companyName')"
            :validator="v$.commercial.contact.companyName" />
          <AkInputMoney v-model="commercial.contact.capital" label="Capital (€)" />
        </div>

        <div class="grid md:grid-cols-2 grid-cols-1 md:gap-4 gap-2">
          <AkInputText v-model="commercial.contact.villeRcs" :label="$t('contact.villeRcs')" :formatter="toUppercase" />
          <AkInputText v-model="commercial.contact.siret" :label="$t('contact.siret')" />
        </div>

        <div class="grid lg:grid-cols-4 md:grid-cols-2 grid-cols-1 md:gap-4 gap-2">
          <AkDropdown
            v-model="commercial.contact.civility"
            :label="$t('contact.civilityManager')"
            :options="this.getCivilitiesConstForProspect()"
            :validator="v$.commercial.contact.civility" />
          <AkInputText
            v-model="commercial.contact.lastName"
            :label="$t('contact.lastNameManager')"
            :formatter="toUppercase"
            :validator="v$.commercial.contact.lastName" />
          <AkInputText
            v-model="commercial.contact.firstName"
            :label="$t('contact.firstNameManager')"
            :validator="v$.commercial.contact.firstName" />
          <AkInputText v-model="commercial.contact.function" :label="$t('contact.functionManager')" />
        </div>

        <div class="grid md:grid-cols-2 grid-cols-1 md:gap-4 gap-2">
          <AkInputText
            v-model="commercial.contact.email"
            :label="$t('contact.email')"
            :validator="v$.commercial.contact.email"
            icon="ga-icon ga-email" />
          <AkInputPhone
            v-model="commercial.contact.phone"
            :label="$t('contact.phone')"
            :validator="v$.commercial.contact.phone" />
        </div>

        <!-- Formulaire adresse -->

        <span class="title-border secondary text-grey-900 text-xl font-bold mb-3">{{
          $t('client.title_address')
        }}</span>
        <AkAddressFull
          v-model="commercial.contact"
          :always-display-map="false"
          :can-change-position="false"
          :label="$t('contact.address1Manager')"
          :validator="v$.commercial" />

        <div class="flex justify-end mt-2">
          <AkButtonValidation ref="btnCreate" :label="$t('add')" class="btn-primary" @action="createCommercial" />
        </div>
      </AkFormSubmitted>
    </template>
  </GaView>
</template>
